import React, { FC, useCallback, useMemo } from "react";
import { StyledTableCell, useStyles } from "./BulkJobs.style";
import TableRow from "@material-ui/core/TableRow";
import { Box, SplitButton } from "common";
import { Badge } from "custom";
import { useDispatch } from "react-redux";
import { useTimeZoneSupport, usePermissions } from "hooks";
import {
  updateBulkBundleJobStatus,
  getBulkBundleResource,
} from "store/actions/bulkBundleUtility.actions";
import { ECampaignActions } from "store/store.interface";
import { format } from "date-fns-tz";
import { IBulkBundleJob } from "store/reducers/bulkBundleUtility.reducers";

type IProps = {
  jobInfo: IBulkBundleJob;
  canCreateCampaign: boolean;
  handleViewErrorModal: (errors: string[]) => void;
};

const BulkJobItem: FC<IProps> = ({
  jobInfo,
  canCreateCampaign,
  handleViewErrorModal,
}) => {
  const { rightAlignCell } = useStyles();
  const dispatch = useDispatch();

  const { dateToTimeZone, timeZone } = useTimeZoneSupport();

  const { id, status, createdBy, createdAt, fileName, noOfCampaigns, errors } =
    jobInfo;

  const onApprove = useCallback(() => {
    dispatch(updateBulkBundleJobStatus(id, "approve"));
  }, [id, dispatch]);

  const onComplete = useCallback(() => {
    dispatch(updateBulkBundleJobStatus(id, "complete"));
  }, [id, dispatch]);

  const onReject = useCallback(() => {
    dispatch(updateBulkBundleJobStatus(id, "reject"));
  }, [id, dispatch]);

  const onFileDownload = useCallback(() => {
    dispatch(getBulkBundleResource(id));
  }, [id, dispatch]);

  const onPause = useCallback(() => {
    dispatch(updateBulkBundleJobStatus(id, "pause"));
  }, [id, dispatch]);

  const onTakeLive = useCallback(() => {
    dispatch(updateBulkBundleJobStatus(id, "live"));
  }, [id, dispatch]);

  const onViewError = useCallback(() => {
    handleViewErrorModal(errors);
  }, [errors, handleViewErrorModal]);

  const availableUserActions = usePermissions(status).filter(
    (action) =>
      !["Get Report", "Download Coupons", "View"].includes(action.title)
  );
  availableUserActions.push({
    title: "File Download",
    action: ECampaignActions.fileDownload,
  });

  if (status === "paused" && !canCreateCampaign) {
    availableUserActions.push({
      title: "Complete",
      action: ECampaignActions.complete,
    });
  }
  if (status === "validation_failed") {
    availableUserActions.push({
      title: "View Error",
      action: ECampaignActions.viewError,
    });
  }

  const options = useMemo(() => {
    if (availableUserActions && availableUserActions.length > 0) {
      const availableActions = availableUserActions.map(({ action, title }) => {
        switch (action) {
          case "approve":
            return {
              title,
              onClick: onApprove,
            };
          case "reject":
            return {
              title,
              onClick: onReject,
            };
          case "complete":
            return {
              title,
              onClick: onComplete,
            };
          case "pause":
            return {
              title,
              onClick: onPause,
            };
          case "takeLive":
            return {
              title,
              onClick: onTakeLive,
            };
          case "fileDownload":
            return {
              title,
              onClick: onFileDownload,
            };
          case "viewError":
            return {
              title,
              onClick: onViewError,
            };
          default:
            return {
              title: "",
              onClick: onFileDownload,
            };
        }
      });
      return availableActions;
    }
    return [];
  }, [
    availableUserActions,
    onApprove,
    onReject,
    onFileDownload,
    onComplete,
    onPause,
    onTakeLive,
    onViewError,
  ]);

  return (
    <TableRow style={{ overflow: "visible" }}>
      <StyledTableCell component="th" scope="row">
        {fileName?.split(".")?.[0]}
      </StyledTableCell>
      <StyledTableCell align="left">{createdBy}</StyledTableCell>
      <StyledTableCell align="left">
        {format(
          dateToTimeZone(createdAt) as Date,
          "MMMM d','yyyy 'at' hh:mm:ss' 'aa",
          { timeZone }
        )}
      </StyledTableCell>
      <StyledTableCell align="center">{noOfCampaigns}</StyledTableCell>
      <StyledTableCell align="center">
        <Badge as={status} borderRadius="5px" width="120px" height="26px" />
      </StyledTableCell>
      <StyledTableCell className={rightAlignCell}>
        <Box>{options?.length > 0 && <SplitButton options={options} />}</Box>
      </StyledTableCell>
    </TableRow>
  );
};

export default BulkJobItem;
